import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  groupKitTemplateControllerGetTemplate,
  groupKitTemplateControllerGetTemplates,
  groupKitTemplateControllerUpdateTemplate,
  groupTemplateControllerGetTemplates,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

export function useKitTemplatesData(
  groupUuid?: string | null,
  kitUuid?: string | null,
) {
  const { data, isPending, error } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid,
    queryKey: queryKeys.kitTemplates(groupUuid!, kitUuid!),
    queryFn: () =>
      groupKitTemplateControllerGetTemplates({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
        },
      }).then((res) => res.data?.data ?? []),
  });

  return { templates: data ?? [], isLoading: isPending, error };
}

export function useKitTemplateData(
  groupUuid?: string | null,
  kitUuid?: string | null,
  templateUuid?: string | null,
) {
  const queryClient = useQueryClient();

  const {
    data: template,
    isLoading,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid && !!templateUuid,
    initialData: null,
    queryKey: queryKeys.kitTemplate(groupUuid!, kitUuid!, templateUuid!),
    queryFn: () =>
      groupKitTemplateControllerGetTemplate({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          templateUuid: templateUuid!,
        },
      }).then((res) => res.data),
  });

  const { mutateAsync: updateTemplate } = useMutation({
    mutationFn: (template: {
      name?: string;
      description?: string;
      instructions?: string;
      scaffolding?: string;
    }) => {
      return groupKitTemplateControllerUpdateTemplate({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          templateUuid: templateUuid!,
        },
        body: {
          name: template.name,
          description: template.description,
          instructions: template.instructions,
          scaffolding: template.scaffolding,
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupTemplates({
          groupUuid: groupUuid!,
          type: "brief_template",
        }),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.kitTemplates(groupUuid!, kitUuid!),
      });
    },
  });

  return { template, isLoading, error, updateTemplate };
}

type ArtifactListAccess = "owner" | "write" | "read";

export function useGroupTemplatesData({
  groupUuid,
  access,
}: {
  groupUuid?: string | null;
  access?: ArtifactListAccess;
}) {
  const {
    data: documents,
    isPending,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid,
    queryKey: queryKeys.groupTemplates({
      groupUuid: groupUuid!,
      access,
    }),
    queryFn: () =>
      groupTemplateControllerGetTemplates({
        path: {
          groupUuid: groupUuid!,
        },
        query: access ? { filter: { access } } : undefined,
      }).then((res) => res.data?.data ?? []),
  });

  return { templates: documents ?? [], isLoading: isPending, error };
}

export function useGroupAndKitTemplatesData({
  groupUuid,
  kitUuid,
  access,
}: {
  groupUuid: string | null;
  kitUuid: string | null;
  access?: ArtifactListAccess;
}) {
  const { templates: kitTemplates, isLoading: isLoadingKitTemplates } =
    useKitTemplatesData(groupUuid, kitUuid);
  const { templates: groupTemplates, isLoading: isLoadingGroupTemplates } =
    useGroupTemplatesData({ groupUuid, access });

  const isLoading = isLoadingKitTemplates || isLoadingGroupTemplates;

  const remainingGroupTemplates = groupTemplates.filter(
    (groupTemplate) =>
      !kitTemplates.some(
        (kitTemplate) => kitTemplate.uuid === groupTemplate.uuid,
      ),
  );

  const allTemplates = [...kitTemplates, ...remainingGroupTemplates];

  return {
    allTemplates,
    kitTemplates,
    groupTemplates,
    remainingGroupTemplates,
    isLoading,
  };
}
